import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { logout } from '../../store/slices/authSlice';
import { fetchItems, fetchCategories } from '../../store/slices/inventorySlice';
import {
  syncCartPrices, syncOfferCartPrices, syncSelectedCategoryWithCategories,
  setOrderMode, setDeliveryPhone, setDeliveryName,
  setDeliveryAddress, setDeliveryRegion, setDeliveryPersonId,
} from '../../store/slices/posSlice';
import { fetchOffers } from '../../store/slices/offersSlice';
import { toggleTheme } from '../../store/slices/themeSlice';
import { closeSession, fetchCurrentSession } from '../../store/slices/sessionSlice';
import { useLanguage } from '../../contexts/LanguageContext';
import { useLatinKeyboardGroupProps } from '../../hooks/useLatinKeyboardGroupProps';
import apiService from '../../services/api';
import { isAdminOrOwner, isManagerOrAbove } from '../../lib/roles';
import {
  subscribeToProductsUpdates, subscribeToCategoriesUpdates,
  subscribeToDeliveryPersonsUpdates, subscribeToStoreUpdates,
  subscribeToUsersUpdates, subscribeToSessionsUpdates,
  subscribeToOffersUpdates,
} from '../../services/socket';
import { fetchUsers } from '../../store/slices/usersSlice';
import {
  ShoppingCart, LayoutDashboard, Package, BarChart3, TrendingUp,
  Settings, LogOut, Sun, Moon, Monitor, Menu, X, RotateCcw,
  FileText, Undo2, Wallet, Users, ShoppingBag, Truck, Copy,
  Eye, EyeOff, Lightbulb, Calendar, Lock, ChevronRight, ListOrdered, Receipt, Tags,
} from 'lucide-react';
import RefundModal from '../refund/RefundModal';
import { printSessionReceipt } from '../pos/Receipt';

// ─── Design tokens (mirrors POS redesign tokens.js) ───────────────────────────

const TOKENS = {
  light: {
    bg: '#f6f6f4',
    surface: '#ffffff',
    surfaceAlt: '#fafaf8',
    sunken: '#efefec',
    text: '#1a1a1f',
    textSecondary: '#5a5a66',
    textMuted: '#8a8a96',
    border: '#e7e7e3',
    borderStrong: '#d8d8d3',
    accent: '#4f46e5',
    accentHover: '#4338ca',
    accentSoft: '#eef2ff',
    accentText: '#3730a3',
    success: '#0d9488',
    successSoft: '#ccfbf1',
    successText: '#115e59',
    danger: '#dc2626',
    dangerSoft: '#fee2e2',
    dangerText: '#991b1b',
    warning: '#d97706',
    warningSoft: '#fef3c7',
    warningText: '#92400e',
    shadow: '0 1px 2px rgba(15,15,25,0.04), 0 4px 12px rgba(15,15,25,0.04)',
  },
  dark: {
    bg: '#13141a',
    surface: '#1a1c23',
    surfaceAlt: '#22242c',
    sunken: '#0f1015',
    text: '#f0f0f5',
    textSecondary: '#a8a8b8',
    textMuted: '#6b6b7a',
    border: '#2a2c36',
    borderStrong: '#363845',
    accent: '#818cf8',
    accentHover: '#6366f1',
    accentSoft: 'rgba(129,140,248,0.12)',
    accentText: '#c7d2fe',
    success: '#2dd4bf',
    successSoft: 'rgba(45,212,191,0.12)',
    successText: '#5eead4',
    danger: '#f87171',
    dangerSoft: 'rgba(248,113,113,0.12)',
    dangerText: '#fca5a5',
    warning: '#fbbf24',
    warningSoft: 'rgba(251,191,36,0.12)',
    warningText: '#fde68a',
    shadow: '0 1px 2px rgba(0,0,0,0.3), 0 4px 12px rgba(0,0,0,0.2)',
  },
  black: {
    bg: '#0a0a0d',
    surface: '#101015',
    surfaceAlt: '#15151c',
    sunken: '#06060a',
    text: '#f5f5fa',
    textSecondary: '#9a9aab',
    textMuted: '#5a5a6a',
    border: '#1f1f28',
    borderStrong: '#2a2a36',
    accent: '#818cf8',
    accentHover: '#a5b4fc',
    accentSoft: 'rgba(129,140,248,0.10)',
    accentText: '#c7d2fe',
    success: '#2dd4bf',
    successSoft: 'rgba(45,212,191,0.10)',
    successText: '#5eead4',
    danger: '#f87171',
    dangerSoft: 'rgba(248,113,113,0.10)',
    dangerText: '#fca5a5',
    warning: '#fbbf24',
    warningSoft: 'rgba(251,191,36,0.10)',
    warningText: '#fde68a',
    shadow: '0 1px 2px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.3)',
  },
};

// ─── Nav group definitions ──────────────────────────────────────────────────

const NAV_GROUP_KEYS = {
  overview: ['/dashboard', '/sessions'],
  operations: ['/reports', '/refunds', '/sales', '/expenses', '/purchases'],
  manage: ['/inventory', '/offers', '/customers', '/delivery-persons', '/monthly-closing', '/audit', '/settings'],
};

// ─── Helper: format duration hh:mm:ss ──────────────────────────────────────

function formatDuration(startedAt) {
  if (!startedAt) return '00:00:00';
  const secs = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000);
  const h = String(Math.floor(secs / 3600)).padStart(2, '0');
  const m = String(Math.floor((secs % 3600) / 60)).padStart(2, '0');
  const s = String(secs % 60).padStart(2, '0');
  return `${h}:${m}:${s}`;
}

function formatSessionStart(startedAt, lang) {
  if (!startedAt) return '';
  const date = new Date(startedAt);
  if (Number.isNaN(date.getTime())) return '';
  const locale = lang === 'ar' ? 'ar-EG' : undefined;
  const datePart = date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  const timePart = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  return `${datePart} · ${timePart}`;
}

// ─── Main Layout component ─────────────────────────────────────────────────

const Layout = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { mode } = useSelector((state) => state.theme);
  const { current: session } = useSelector((state) => state.session);
  const pos = useSelector((state) => state.pos);
  const activeOrderMode = pos.orders.find(o => o.id === pos.activeOrderId)?.mode || 'in_store';
  const sessionRef = React.useRef(session);
  sessionRef.current = session;
  const userRef = React.useRef(user);
  userRef.current = user;
  const { t, language, isRTL } = useLanguage();
  const { containerProps: closeSessionLatinKeyboardProps } = useLatinKeyboardGroupProps();

  const [zoomLevel, setZoomLevel] = React.useState(1.0);

  React.useEffect(() => {
    document.documentElement.style.zoom = zoomLevel;
    return () => { document.documentElement.style.zoom = 1; };
  }, [zoomLevel]);

  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (!e.ctrlKey) return;
      if (e.key === '=' || e.key === '+') {
        e.preventDefault();
        setZoomLevel((prev) => Math.min(+(prev + 0.1).toFixed(1), 2.0));
      } else if (e.key === '-') {
        e.preventDefault();
        setZoomLevel((prev) => Math.max(+(prev - 0.1).toFixed(1), 0.5));
      } else if (e.key === '0') {
        e.preventDefault();
        setZoomLevel(1.0);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const [showCloseSessionModal, setShowCloseSessionModal] = React.useState(false);
  const [closePassword, setClosePassword] = React.useState('');
  const [showClosePassword, setShowClosePassword] = React.useState(false);
  const [closeLoading, setCloseLoading] = React.useState(false);
  const [closeError, setCloseError] = React.useState(null);
  const [showRefundModal, setShowRefundModal] = React.useState(false);
  const [showDeliveryPersonsModal, setShowDeliveryPersonsModal] = React.useState(false);
  const [deliveryPersons, setDeliveryPersons] = React.useState([]);
  const [copiedId, setCopiedId] = React.useState(null);
  const [storeInfo, setStoreInfo] = React.useState({ storeName: '', logoPath: '', logoSize: 100 });
  const [sessionTimer, setSessionTimer] = React.useState('00:00:00');

  // ─── Live session timer ───────────────────────────────────────────────────

  React.useEffect(() => {
    if (!session?.started_at) return;
    setSessionTimer(formatDuration(session.started_at));
    const id = setInterval(() => setSessionTimer(formatDuration(session.started_at)), 1000);
    return () => clearInterval(id);
  }, [session?.started_at]);

  // ─── Data subscriptions ───────────────────────────────────────────────────

  const loadStoreInfo = React.useCallback(async () => {
    try {
      const res = await apiService.getStore();
      const data = res.data || res;
      const ls = parseInt(data.logo_size, 10);
      setStoreInfo({
        storeName: data.name || '',
        logoPath: data.logo_path || '',
        logoSize: Number.isFinite(ls) && ls >= 40 && ls <= 400 ? ls : 100,
      });
    } catch (err) {
      console.error('Failed to load store info', err);
    }
  }, []);

  React.useEffect(() => { loadStoreInfo(); }, [loadStoreInfo]);

  React.useEffect(() => {
    const unsubscribe = subscribeToStoreUpdates(loadStoreInfo);
    return unsubscribe;
  }, [loadStoreInfo]);

  React.useEffect(() => {
    const unsubscribe = subscribeToUsersUpdates(() => {
      dispatch(fetchUsers(true)).catch(() => {});
    });
    return unsubscribe;
  }, [dispatch]);

  React.useEffect(() => {
    const unsubscribe = subscribeToSessionsUpdates(() => {
      const prevSession = sessionRef.current;
      const currentUser = userRef.current;
      const hadOpenSession = prevSession && prevSession.status === 'open';
      dispatch(fetchCurrentSession())
        .unwrap()
        .then((newSession) => {
          const ownedOpenSession =
            hadOpenSession &&
            currentUser?.role === 'cashier' &&
            String(prevSession.cashier_id) === String(currentUser.id);
          if (ownedOpenSession && newSession === null) dispatch(logout());
        })
        .catch(() => {});
    });
    return unsubscribe;
  }, [dispatch]);

  React.useEffect(() => {
    const unsubscribe = subscribeToProductsUpdates(() => {
      dispatch(fetchItems())
        .unwrap()
        .then((items) => dispatch(syncCartPrices(items)))
        .catch(() => {});
    });
    return unsubscribe;
  }, [dispatch]);

  React.useEffect(() => {
    const unsubscribe = subscribeToCategoriesUpdates(() => {
      dispatch(fetchCategories())
        .unwrap()
        .then((categories) => dispatch(syncSelectedCategoryWithCategories(categories)))
        .catch(() => {});
      dispatch(fetchItems())
        .unwrap()
        .then((items) => dispatch(syncCartPrices(items)))
        .catch(() => {});
    });
    return unsubscribe;
  }, [dispatch]);

  React.useEffect(() => {
    const unsubscribe = subscribeToOffersUpdates(() => {
      dispatch(fetchOffers(true))
        .unwrap()
        .then((offers) => dispatch(syncOfferCartPrices(offers)))
        .catch(() => {});
    });
    return unsubscribe;
  }, [dispatch]);

  const loadDeliveryPersons = React.useCallback(async () => {
    try {
      const res = await apiService.getDeliveryPersons();
      setDeliveryPersons(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setDeliveryPersons([]);
    }
  }, []);

  React.useEffect(() => {
    const unsubscribe = subscribeToDeliveryPersonsUpdates(loadDeliveryPersons);
    return unsubscribe;
  }, [loadDeliveryPersons]);

  const handleOpenDeliveryPersons = () => {
    loadDeliveryPersons();
    setShowDeliveryPersonsModal(true);
  };

  // ─── Close session handler ────────────────────────────────────────────────

  const handleConfirmCloseSession = async (e) => {
    e.preventDefault();
    if (!closePassword) {
      setCloseError(t('login.password') + ' ' + t('errors.validationError'));
      return;
    }
    const username = (user?.username || '').trim();
    if (!username) {
      setCloseError(t('login.incorrectPassword'));
      return;
    }
    setCloseLoading(true);
    setCloseError(null);
    try {
      await apiService.login(username, closePassword);
      const closedSession = await dispatch(closeSession({})).unwrap();
      await dispatch(fetchCurrentSession());
      setShowCloseSessionModal(false);
      setClosePassword('');
      setShowClosePassword(false);
      if (closedSession) await printSessionReceipt(closedSession, storeInfo);
    } catch (err) {
      const raw = err?.response?.data?.error || err?.message || '';
      const isInvalidCreds = /invalid credentials|invalid username|invalid password/i.test(raw);
      setCloseError(isInvalidCreds ? t('login.incorrectPassword') : raw || t('login.incorrectPassword'));
    } finally {
      setCloseLoading(false);
    }
  };

  // ─── Derived state ────────────────────────────────────────────────────────

  const isManagerOrAdmin = isManagerOrAbove(user?.role);
  const isAdmin = isAdminOrOwner(user?.role);
  const tk = TOKENS[mode] || TOKENS.light;
  const dir = isRTL ? 'rtl' : 'ltr';

  const navigationItems = isManagerOrAdmin
    ? [
        { path: '/dashboard',        icon: Lightbulb,       label: t('nav.dashboard') },
        { path: '/sessions',         icon: LayoutDashboard, label: t('nav.sessions') },
        { path: '/reports',          icon: BarChart3,        label: t('nav.orders') },
        { path: '/refunds',          icon: Undo2,            label: t('nav.refunds') },
        { path: '/sales',            icon: TrendingUp,       label: t('nav.sales') },
        { path: '/expenses',         icon: Wallet,           label: t('nav.expenses') },
        { path: '/purchases',        icon: ShoppingBag,      label: t('nav.purchases') },
        { path: '/inventory',        icon: Package,          label: t('nav.inventory') },
        { path: '/offers',           icon: Tags,             label: t('nav.offers') },
        { path: '/customers',        icon: Users,            label: t('nav.customers') },
        { path: '/delivery-persons', icon: Truck,            label: t('nav.deliveryPersonnel') },
        { path: '/monthly-closing',  icon: Calendar,         label: t('nav.monthlyClosing') },
        ...(isAdmin ? [{ path: '/audit', icon: FileText, label: t('nav.audit') }] : []),
        { path: '/settings',         icon: Settings,         label: t('nav.settings') },
      ]
    : [{ path: '/pos', icon: ShoppingCart, label: t('nav.pos') }];

  const getRoleLabel = (role) => {
    if (role === 'owner') return t('settings.owner');
    if (role === 'admin') return t('settings.admin');
    if (role === 'manager') return t('settings.manager');
    if (role === 'cashier') return t('settings.cashier');
    return role;
  };

  const userInitials = (user?.full_name || '?')
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  // Group nav items for admin sidebar
  const navGroups = [
    {
      key: 'overview',
      title: language === 'ar' ? 'نظرة عامة' : 'Overview',
      items: navigationItems.filter((i) => NAV_GROUP_KEYS.overview.includes(i.path)),
    },
    {
      key: 'operations',
      title: language === 'ar' ? 'العمليات' : 'Operations',
      items: navigationItems.filter((i) => NAV_GROUP_KEYS.operations.includes(i.path)),
    },
    {
      key: 'manage',
      title: language === 'ar' ? 'الإدارة' : 'Manage',
      items: navigationItems.filter((i) => NAV_GROUP_KEYS.manage.includes(i.path)),
    },
  ].filter((g) => g.items.length > 0);

  // ─── Shared modal styles ──────────────────────────────────────────────────

  const overlayStyle = {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 50,
  };

  const modalStyle = {
    background: tk.surface,
    border: `1px solid ${tk.border}`,
    borderRadius: 14,
    padding: '24px',
    maxWidth: 420,
    width: '100%',
    margin: '0 16px',
  };

  // ─── Admin shell ──────────────────────────────────────────────────────────

  if (isManagerOrAdmin) {
    return (
      <div
        className={`theme-${mode}`}
        style={{ display: 'flex', height: '100%', background: tk.bg, color: tk.text, direction: dir,
          fontFamily: language === 'ar' ? '"Cairo", sans-serif' : '"DM Sans", sans-serif' }}
      >
        {/* Sidebar */}
        <aside
          style={{
            width: sidebarCollapsed ? 60 : 232,
            flexShrink: 0,
            background: tk.surface,
            borderInlineEnd: `1px solid ${tk.border}`,
            display: 'flex',
            flexDirection: 'column',
            transition: 'width 0.2s ease',
            overflow: 'hidden',
          }}
        >
          {/* Sidebar header */}
          <div
            style={{
              height: sidebarCollapsed ? 'auto' : 60, 
              flexShrink: 0,
              padding: sidebarCollapsed ? '12px 10px' : '0 16px',
              borderBottom: `1px solid ${tk.border}`,
              display: 'flex', 
              flexDirection: sidebarCollapsed ? 'column' : 'row',
              alignItems: sidebarCollapsed ? 'center' : 'center',
              justifyContent: sidebarCollapsed ? 'flex-start' : 'space-between',
              gap: sidebarCollapsed ? 10 : 0,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <div
                style={{
                  width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <img
                  src={mode === 'light' ? process.env.PUBLIC_URL + '/outlined_icon_black.svg' : process.env.PUBLIC_URL + '/outlined_icon_white.svg'}
                  alt="Numuw Counter"
                  style={{
                    width: '100%', height: '100%',
                    objectFit: 'contain',
                  }}
                />
              </div>
              {!sidebarCollapsed && (
                <div>
                  <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 20, fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {isRTL ? (t('login.title') || 'كاونتر') : 'Counter'}
                  </div>
                </div>
              )}
            </div>
            {!sidebarCollapsed && (
              <button
                onClick={() => setSidebarCollapsed(true)}
                style={{
                  width: 26, height: 26, border: `1px solid ${tk.border}`,
                  background: tk.surfaceAlt, borderRadius: 6, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: tk.textMuted,
                }}
              >
                <X size={13} />
              </button>
            )}
            {sidebarCollapsed && (
              <button
                onClick={() => setSidebarCollapsed(false)}
                style={{
                  width: 26, height: 26, border: `1px solid ${tk.border}`,
                  background: tk.surfaceAlt, borderRadius: 6, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: tk.textMuted,
                }}
              >
                <Menu size={13} />
              </button>
            )}
          </div>

          {/* User card */}
          {!sidebarCollapsed && (
            <div
              style={{
                margin: '10px 12px',
                padding: '10px 12px',
                borderRadius: 10,
                background: tk.surfaceAlt,
                border: `1px solid ${tk.border}`,
                display: 'flex', alignItems: 'center', gap: 10,
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  width: 30, height: 30, borderRadius: 7, flexShrink: 0,
                  background: tk.accentSoft, color: tk.accentText,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 600, fontSize: 11.5,
                }}
              >
                {userInitials}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    fontSize: 12.5, fontWeight: 600,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    color: tk.text,
                  }}
                >
                  {user?.full_name}
                </div>
                <span
                  style={{
                    display: 'inline-block', fontSize: 9.5, fontWeight: 600,
                    padding: '1px 6px', borderRadius: 4, marginTop: 2,
                    background: tk.accentSoft, color: tk.accentText,
                    textTransform: 'uppercase', letterSpacing: 0.4,
                  }}
                >
                  {getRoleLabel(user?.role)}
                </span>
              </div>
            </div>
          )}

          {/* Nav groups */}
          <nav
            className="sidebar-nav"
            style={{
              flex: 1, overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none',
              padding: sidebarCollapsed ? '4px' : '4px 10px',
            }}
          >
            {navGroups.map((group) => (
              <div key={group.key} style={{ marginBottom: 16 }}>
                {!sidebarCollapsed && (
                  <div
                    style={{
                      fontSize: 10, fontWeight: 600, color: tk.textMuted,
                      textTransform: 'uppercase', letterSpacing: 0.6,
                      padding: '6px 10px 4px',
                    }}
                  >
                    {group.title}
                  </div>
                )}
                {group.items.map((item) => {
                  const active = location.pathname === item.path;
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      title={sidebarCollapsed ? item.label : ''}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: sidebarCollapsed ? '9px 0' : '7px 10px',
                        justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                        background: active ? tk.accentSoft : 'transparent',
                        color: active ? tk.accentText : tk.textSecondary,
                        border: 'none',
                        borderRadius: 7,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: 12.5,
                        fontWeight: active ? 600 : 500,
                        marginBottom: 1,
                        position: 'relative',
                        textAlign: 'start',
                      }}
                    >
                      {active && !sidebarCollapsed && (
                        <span
                          style={{
                            position: 'absolute',
                            insetInlineStart: 0,
                            top: 7, bottom: 7,
                            width: 2.5,
                            background: tk.accent,
                            borderRadius: 99,
                          }}
                        />
                      )}
                      <Icon size={15} />
                      {!sidebarCollapsed && <span>{item.label}</span>}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* Sidebar footer */}
          <div
            style={{
              padding: sidebarCollapsed ? 6 : 10,
              borderTop: `1px solid ${tk.border}`,
              flexShrink: 0,
            }}
          >
            <button
              onClick={() => dispatch(toggleTheme())}
              onMouseDown={(e) => e.preventDefault()}
              title={sidebarCollapsed ? t('nav.theme') : ''}
              style={{
                width: '100%', display: 'flex', alignItems: 'center',
                gap: 10, padding: sidebarCollapsed ? '8px 0' : '7px 10px',
                justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                background: 'transparent', color: tk.textSecondary,
                border: 'none', borderRadius: 7, cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 12.5, fontWeight: 500, marginBottom: 1,
              }}
            >
              {mode === 'black' ? <Sun size={15} /> : mode === 'light' ? <Moon size={15} /> : <Monitor size={15} />}
              {!sidebarCollapsed && (
                mode === 'black' ? t('nav.light') : mode === 'light' ? t('nav.dark') : t('nav.black')
              )}
            </button>
            <button
              onClick={() => dispatch(logout())}
              title={sidebarCollapsed ? t('nav.logout') : ''}
              style={{
                width: '100%', display: 'flex', alignItems: 'center',
                gap: 10, padding: sidebarCollapsed ? '8px 0' : '7px 10px',
                justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                background: 'transparent', color: tk.danger,
                border: 'none', borderRadius: 7, cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 12.5, fontWeight: 500,
              }}
            >
              <LogOut size={15} />
              {!sidebarCollapsed && t('nav.logout')}
            </button>
          </div>
        </aside>

        {/* Main area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <main
            style={{
              flex: 1, minHeight: 0, overflowY: 'auto',
              padding: 20, background: tk.bg,
            }}
          >
            {children}
          </main>
        </div>

        {/* Modals */}
        {renderModals({ tk, dir, t, language,
          showCloseSessionModal, setShowCloseSessionModal,
          closePassword, setClosePassword, showClosePassword, setShowClosePassword,
          closeLoading, closeError, setCloseError, handleConfirmCloseSession,
          showDeliveryPersonsModal, setShowDeliveryPersonsModal,
          deliveryPersons, copiedId, setCopiedId,
          showRefundModal, setShowRefundModal,
          mode, overlayStyle, modalStyle,
        })}
      </div>
    );
  }

  // ─── Cashier shell ────────────────────────────────────────────────────────

  return (
    <div
      className={`theme-${mode}`}
      style={{
        display: 'flex', flexDirection: 'column', height: '100%',
        background: tk.bg, color: tk.text, direction: dir,
        fontFamily: language === 'ar' ? '"Cairo", sans-serif' : '"DM Sans", sans-serif',
      }}
    >
      {/* Cashier header — 52px */}
      <header
        style={{
          height: 52, flexShrink: 0,
          padding: '0 16px',
          background: tk.surface,
          borderBottom: `1px solid ${tk.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        {/* Left: cashier identity + session status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {/* Avatar + name + role */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <img
              src={mode === 'light' ? process.env.PUBLIC_URL + '/outlined_icon_black.svg' : process.env.PUBLIC_URL + '/outlined_icon_white.svg'}
              alt="User"
              style={{
                width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                objectFit: 'contain',
              }}
            />
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: tk.text, lineHeight: 1.1 }}>
                {user?.full_name}
              </div>
              <div style={{ fontSize: 10, color: tk.textMuted, marginTop: 2, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                {getRoleLabel(user?.role)}
              </div>
            </div>
          </div>

          <span style={{ width: 1, height: 18, background: tk.border, flexShrink: 0 }} />

          {/* Session status — no ID, no timer */}
          {session?.status === 'open' ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12 }}>
              <span
                style={{
                  width: 7, height: 7, borderRadius: 99,
                  background: tk.success,
                  boxShadow: `0 0 8px ${tk.success}`,
                  flexShrink: 0,
                }}
              />
              <span style={{ color: tk.textSecondary, fontWeight: 500 }}>
                {language === 'ar' ? 'الجلسة مفتوحة' : 'Session open'}
              </span>
              {(session?.start_time || session?.started_at) && (
                <>
                  <span style={{ color: tk.textMuted, fontWeight: 500 }}>·</span>
                  <span style={{ color: tk.textMuted, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                    {formatSessionStart(session.start_time || session.started_at, language)}
                  </span>
                </>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12 }}>
              <span
                style={{
                  width: 7, height: 7, borderRadius: 99,
                  background: tk.textMuted, flexShrink: 0,
                }}
              />
              <span style={{ color: tk.textMuted }}>
                {language === 'ar' ? 'لا توجد جلسة' : 'No session'}
              </span>
            </div>
          )}

{/* Session Orders / Refunds moved to right side */}
        </div>

        {/* Right: session nav + mode toggle + actions + utilities */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>

          {/* ── Session page navigation ─────────────────────────── */}
          {session?.status === 'open' && location.pathname === '/pos' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new Event('pos:open-session-orders-page'))}
                  style={headerActionBtn(tk)}
                >
                  <ListOrdered size={13} />
                  <span style={{ fontSize: 11.5, fontWeight: 500 }}>{t('pos.sessionOrders')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new Event('pos:open-session-refunds-page'))}
                  style={headerActionBtn(tk)}
                >
                  <Receipt size={13} />
                  <span style={{ fontSize: 11.5, fontWeight: 500 }}>{t('pos.sessionRefunds')}</span>
                </button>
              </div>
              <span style={{ width: 1, height: 18, background: tk.border, flexShrink: 0 }} />
            </>
          )}

          {/* ── Quick actions ─────────────────────────────────────── */}
          {session?.status === 'open' && (
            <>
              <button onClick={handleOpenDeliveryPersons} style={headerActionBtn(tk)} title={t('pos.deliveryPersonnelList')}>
                <Truck size={13} />
                <span style={{ fontSize: 11.5, fontWeight: 500 }}>{language === 'ar' ? 'المندوبين' : 'Drivers'}</span>
              </button>
              <button onClick={() => setShowRefundModal(true)} style={headerActionBtn(tk)}>
                <RotateCcw size={13} />
                <span style={{ fontSize: 11.5, fontWeight: 500 }}>{t('pos.refund')}</span>
              </button>
              <span style={{ width: 1, height: 18, background: tk.border, flexShrink: 0 }} />
              <button
                onClick={() => { setShowCloseSessionModal(true); setCloseError(null); setClosePassword(''); }}
                style={{ ...headerActionBtn(tk), background: tk.warningSoft, borderColor: `${tk.warning}44`, color: tk.warning }}>
                <X size={13} />
                <span style={{ fontSize: 11.5, fontWeight: 600 }}>{t('pos.closeSession')}</span>
              </button>
              <span style={{ width: 1, height: 18, background: tk.border, flexShrink: 0 }} />
            </>
          )}

          {/* ── Utilities ─────────────────────────────────────────── */}
          <button onClick={() => dispatch(toggleTheme())}
            onMouseDown={(e) => e.preventDefault()}
            style={{ width: 28, height: 28, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, borderRadius: 6, color: tk.textSecondary, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            title={t('nav.theme')}>
            {mode === 'black' ? <Sun size={13} /> : mode === 'light' ? <Moon size={13} /> : <Monitor size={13} />}
          </button>
          <button onClick={() => dispatch(logout())}
            style={{ width: 28, height: 28, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, borderRadius: 6, color: tk.danger, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            title={t('nav.logout')}>
            <LogOut size={13} />
          </button>
        </div>
      </header>

      {/* Content */}
      <main style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {children}
      </main>

      {/* Shortcut strip — 28px */}
      <footer
        style={{
          height: 28, flexShrink: 0,
          padding: '0 16px',
          background: tk.surface,
          borderTop: `1px solid ${tk.border}`,
          display: 'flex', alignItems: 'center', gap: 14,
          fontSize: 10.5, color: tk.textMuted,
          overflowX: 'auto', whiteSpace: 'nowrap',
        }}
      >
        {[
          ['F1', language === 'ar' ? 'فاتورة جديدة' : 'New sale'],
          ['F4', language === 'ar' ? 'توصيل' : 'Delivery'],
          ['F6', language === 'ar' ? 'دفع' : 'Pay'],
          ['F8', language === 'ar' ? 'باركود' : 'Barcode'],
          ['F9', language === 'ar' ? 'بحث' : 'Search'],
          ['F10', language === 'ar' ? 'طريقة الدفع' : 'Payment method'],
          ['F11/F12', language === 'ar' ? 'تنقّل بين الطلبات' : 'Switch order'],
          ['Ctrl+Q', language === 'ar' ? 'تعديل كمية آخر منتج' : 'Edit last item qty'],
          ['Ctrl+D', language === 'ar' ? 'حذف آخر منتج' : 'Remove last item'],
          ['Ctrl+±', language === 'ar' ? 'تكبير/تصغير' : 'Zoom'],
        ].map(([key, label]) => (
          <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <kbd
              dir="ltr"
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                height: 16, padding: '0 5px',
                background: tk.sunken, border: `1px solid ${tk.border}`,
                borderRadius: 3, fontSize: 9.5, fontFamily: '"JetBrains Mono", monospace',
                color: tk.textMuted, fontWeight: 500,
                unicodeBidi: 'isolate',
              }}
            >
              {key}
            </kbd>
            {label}
          </span>
        ))}
      </footer>

      {/* Modals */}
      {renderModals({ tk, dir, t, language,
        closeSessionLatinKeyboardProps,
        showCloseSessionModal, setShowCloseSessionModal,
        closePassword, setClosePassword, showClosePassword, setShowClosePassword,
        closeLoading, closeError, setCloseError, handleConfirmCloseSession,
        showDeliveryPersonsModal, setShowDeliveryPersonsModal,
        deliveryPersons, copiedId, setCopiedId,
        showRefundModal, setShowRefundModal,
        mode, overlayStyle, modalStyle,
      })}
    </div>
  );
};

// ─── Header action button style ────────────────────────────────────────────

function headerActionBtn(tk) {
  return {
    display: 'flex', alignItems: 'center', gap: 5,
    padding: '5px 9px', height: 28,
    background: tk.surfaceAlt, border: `1px solid ${tk.border}`,
    borderRadius: 6, cursor: 'pointer', color: tk.textSecondary,
    fontFamily: 'inherit',
  };
}

// ─── Shared modal renderer ─────────────────────────────────────────────────

function renderModals({
  tk, dir, t, language,
  closeSessionLatinKeyboardProps,
  showCloseSessionModal, setShowCloseSessionModal,
  closePassword, setClosePassword, showClosePassword, setShowClosePassword,
  closeLoading, closeError, setCloseError, handleConfirmCloseSession,
  showDeliveryPersonsModal, setShowDeliveryPersonsModal,
  deliveryPersons, copiedId, setCopiedId,
  showRefundModal, setShowRefundModal,
  mode, overlayStyle, modalStyle,
}) {
  return (
    <>
      {/* Close session modal */}
      {showCloseSessionModal && (
        <div style={overlayStyle}>
          <div style={modalStyle} dir={dir}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0, color: tk.text }}>
                {t('sessions.closeSession')}
              </h3>
              <button
                onClick={() => { setShowCloseSessionModal(false); setClosePassword(''); setCloseError(null); setShowClosePassword(false); }}
                style={{
                  width: 28, height: 28, border: `1px solid ${tk.border}`,
                  background: tk.surfaceAlt, borderRadius: 6, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: tk.textMuted,
                }}
              >
                <X size={14} />
              </button>
            </div>

            <p style={{ fontSize: 13, color: tk.textSecondary, marginBottom: 16, marginTop: 0 }}>
              {t('login.password')}
            </p>

            {closeError && (
              <div
                style={{
                  background: tk.dangerSoft, border: `1px solid ${tk.danger}33`,
                  borderRadius: 8, padding: '10px 12px', marginBottom: 14,
                  color: tk.dangerText, fontSize: 13,
                }}
              >
                {closeError}
              </div>
            )}

            <form onSubmit={handleConfirmCloseSession} {...closeSessionLatinKeyboardProps}>
              <div style={{ position: 'relative', marginBottom: 16 }}>
                <div
                  style={{
                    position: 'absolute', insetBlockStart: 0, insetBlockEnd: 0,
                    insetInlineStart: 0,
                    display: 'flex', alignItems: 'center', paddingInlineStart: 12,
                    pointerEvents: 'none',
                  }}
                >
                  <Lock size={15} color={tk.textMuted} />
                </div>
                <input
                  type={showClosePassword ? 'text' : 'password'}
                  name="password"
                  maxLength={50}
                  placeholder={t('login.password')}
                  value={closePassword}
                  onChange={(e) => setClosePassword(e.target.value)}
                  dir={dir}
                  lang={language}
                  autoComplete="current-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  style={{
                    width: '100%', height: 40,
                    paddingInlineStart: 38, paddingInlineEnd: 38,
                    background: tk.sunken, border: `1px solid ${tk.border}`,
                    borderRadius: 8, color: tk.text, fontSize: 13,
                    fontFamily: 'inherit', outline: 'none',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowClosePassword((p) => !p)}
                  tabIndex={-1}
                  style={{
                    position: 'absolute', insetBlockStart: 0, insetBlockEnd: 0,
                    insetInlineEnd: 0,
                    display: 'flex', alignItems: 'center', paddingInlineEnd: 12,
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: tk.textMuted,
                  }}
                >
                  {showClosePassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => { setShowCloseSessionModal(false); setClosePassword(''); setCloseError(null); setShowClosePassword(false); }}
                  style={{
                    padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
                    border: `1px solid ${tk.border}`, background: 'transparent',
                    color: tk.textSecondary, fontSize: 13, fontFamily: 'inherit', fontWeight: 500,
                  }}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={closeLoading}
                  style={{
                    padding: '8px 16px', borderRadius: 8, cursor: closeLoading ? 'not-allowed' : 'pointer',
                    border: 'none', background: tk.danger,
                    color: '#fff', fontSize: 13, fontFamily: 'inherit', fontWeight: 600,
                    opacity: closeLoading ? 0.6 : 1,
                  }}
                >
                  {closeLoading ? t('common.loading') : t('common.confirm')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delivery persons modal */}
      {showDeliveryPersonsModal && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }} dir={dir}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexShrink: 0 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0, color: tk.text }}>
                {t('pos.deliveryPersonnelList')}
              </h3>
              <button
                onClick={() => setShowDeliveryPersonsModal(false)}
                style={{
                  width: 28, height: 28, border: `1px solid ${tk.border}`,
                  background: tk.surfaceAlt, borderRadius: 6, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: tk.textMuted,
                }}
              >
                <X size={14} />
              </button>
            </div>

            <div style={{ overflowY: 'auto', flex: 1 }}>
              {deliveryPersons.length === 0 ? (
                <p style={{ fontSize: 13, color: tk.textMuted }}>{t('settings.noDeliveryPersons')}</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {deliveryPersons.map((dp) => (
                    <div
                      key={dp.id}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: 12, padding: '10px 12px', borderRadius: 8,
                        background: tk.surfaceAlt, border: `1px solid ${tk.border}`,
                      }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 500, color: tk.text }}>{dp.name}</span>
                      {dp.phone ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <a
                            href={`tel:${dp.phone}`}
                            style={{ fontSize: 12, color: tk.accentText, textDecoration: 'none',
                              fontFamily: '"JetBrains Mono", monospace' }}
                          >
                            {dp.phone}
                          </a>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(dp.phone);
                              setCopiedId(dp.id);
                              setTimeout(() => setCopiedId(null), 1500);
                            }}
                            style={{
                              width: 24, height: 24, border: `1px solid ${tk.border}`,
                              background: 'transparent', borderRadius: 5, cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: tk.textMuted,
                            }}
                          >
                            <Copy size={12} />
                          </button>
                          {copiedId === dp.id && (
                            <span style={{ fontSize: 11, color: tk.success, fontWeight: 600 }}>
                              {t('common.copied')}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span style={{ fontSize: 12, color: tk.textMuted }}>—</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Refund modal */}
      {showRefundModal && (
        <RefundModal
          isOpen={showRefundModal}
          onClose={() => setShowRefundModal(false)}
          mode={mode}
          themeSidebar={mode === 'black' ? '#101015' : mode === 'dark' ? '#1a1c23' : '#ffffff'}
          themeBorder={mode === 'black' ? '#1f1f28' : mode === 'dark' ? '#2a2c36' : '#e7e7e3'}
          themeText={mode === 'light' ? '#1a1a1f' : '#f0f0f5'}
          themeTextSecondary={mode === 'light' ? '#5a5a66' : '#a8a8b8'}
        />
      )}
    </>
  );
}

export default Layout;
